import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Rocket, Zap, Star, Crown, Check, Loader2, Eye, TrendingUp, Clock, Shield } from 'lucide-react';
import axios from '../lib/axios';
import { toast } from 'sonner';
import { API, useAuth } from '../App';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

// Promotion packages
const PACKAGES = [
  {
    id: 'basic',
    name: 'Basic Boost',
    price: 5,
    duration: '7 days',
    icon: Zap,
    color: 'blue',
    features: [
      'Appear in Featured section',
      '3x more visibility',
      'Featured badge on listing',
    ],
    popular: false,
  },
  {
    id: 'standard',
    name: 'Standard Boost',
    price: 10,
    duration: '14 days',
    icon: Star,
    color: 'purple',
    features: [
      'Everything in Basic',
      '5x more visibility',
      'Priority in search results',
      'Highlighted listing card',
    ],
    popular: true,
  },
  {
    id: 'premium',
    name: 'Premium Boost',
    price: 25,
    duration: '30 days',
    icon: Crown,
    color: 'amber',
    features: [
      'Everything in Standard',
      '10x more visibility',
      'Top position in category',
      'Homepage featured carousel',
      'Social media promotion',
    ],
    popular: false,
  },
];

const PromoteListingPage = () => {
  const { listingId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [promoting, setPromoting] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState('standard');

  useEffect(() => {
    const fetchListing = async () => {
      try {
        const response = await axios.get(`${API}/listings/${listingId}`);
        setListing(response.data);
        
        // Check if user owns this listing
        if (user && response.data.user_id !== user.id) {
          toast.error('You can only promote your own listings');
          navigate('/my-listings');
        }
      } catch (error) {
        toast.error('Listing not found');
        navigate('/my-listings');
      } finally {
        setLoading(false);
      }
    };

    if (listingId) {
      fetchListing();
    }
  }, [listingId, user, navigate]);

  const handlePromote = async () => {
    if (!selectedPackage) {
      toast.error('Please select a promotion package');
      return;
    }

    setPromoting(true);
    try {
      const response = await axios.post(`${API}/promotions/checkout`, {
        listing_id: listingId,
        package_id: selectedPackage,
        origin_url: window.location.origin,
      });

      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to start checkout';
      toast.error(message);
      setPromoting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center py-32">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="container mx-auto px-4 py-8 max-w-5xl">
        {/* Back button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back</span>
        </button>

        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10 mb-4">
            <Rocket className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-foreground mb-3">
            Boost Your Listing
          </h1>
          <p className="text-muted-foreground max-w-lg mx-auto">
            Get more eyes on your listing and sell faster with our promotion packages
          </p>
        </div>

        {/* Listing preview */}
        {listing && (
          <div className="bg-card rounded-2xl border border-border p-4 mb-8 flex items-center gap-4">
            <div className="w-20 h-20 rounded-xl overflow-hidden flex-shrink-0 bg-muted">
              {listing.images?.[0] ? (
                <img 
                  src={listing.images[0]} 
                  alt={listing.title}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                  No image
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-foreground truncate">{listing.title}</h3>
              <p className="text-sm text-muted-foreground">
                {listing.pricing?.currency} {listing.pricing?.amount?.toLocaleString()}
              </p>
              <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Eye className="w-3 h-3" />
                  {listing.view_count || 0} views
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Packages */}
        <div className="grid md:grid-cols-3 gap-4 mb-8">
          {PACKAGES.map((pkg) => {
            const Icon = pkg.icon;
            const isSelected = selectedPackage === pkg.id;
            const colorClasses = {
              blue: 'border-blue-500 bg-blue-500/5',
              purple: 'border-purple-500 bg-purple-500/5',
              amber: 'border-amber-500 bg-amber-500/5',
            };
            const iconColorClasses = {
              blue: 'text-blue-500 bg-blue-500/10',
              purple: 'text-purple-500 bg-purple-500/10',
              amber: 'text-amber-500 bg-amber-500/10',
            };

            return (
              <button
                key={pkg.id}
                onClick={() => setSelectedPackage(pkg.id)}
                className={`relative p-6 rounded-2xl border-2 text-left transition-all hover:shadow-lg ${
                  isSelected 
                    ? colorClasses[pkg.color] + ' ring-2 ring-offset-2 ring-offset-background ring-primary'
                    : 'border-border bg-card hover:border-muted-foreground/30'
                }`}
                data-testid={`package-${pkg.id}`}
              >
                {/* Popular badge */}
                {pkg.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-purple-500 text-white text-xs font-bold rounded-full">
                    MOST POPULAR
                  </div>
                )}

                {/* Icon */}
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${iconColorClasses[pkg.color]}`}>
                  <Icon className="w-6 h-6" />
                </div>

                {/* Name & Price */}
                <h3 className="text-lg font-bold text-foreground mb-1">{pkg.name}</h3>
                <div className="flex items-baseline gap-1 mb-1">
                  <span className="text-3xl font-bold text-foreground">${pkg.price}</span>
                  <span className="text-muted-foreground text-sm">/ {pkg.duration}</span>
                </div>

                {/* Features */}
                <ul className="space-y-2 mt-4">
                  {pkg.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <Check className={`w-4 h-4 mt-0.5 flex-shrink-0 ${isSelected ? 'text-primary' : 'text-green-500'}`} />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* Selection indicator */}
                {isSelected && (
                  <div className="absolute top-4 right-4 w-6 h-6 rounded-full bg-primary flex items-center justify-center">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* Trust badges */}
        <div className="flex flex-wrap items-center justify-center gap-6 mb-8 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4 text-green-500" />
            <span>Secure Payment</span>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-blue-500" />
            <span>Instant Activation</span>
          </div>
          <div className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-purple-500" />
            <span>Proven Results</span>
          </div>
        </div>

        {/* CTA Button */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <button
            onClick={handlePromote}
            disabled={promoting || !selectedPackage}
            className="w-full sm:w-auto px-8 py-4 bg-primary hover:bg-primary/90 text-white font-bold rounded-xl transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="promote-checkout-btn"
          >
            {promoting ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Processing...</span>
              </>
            ) : (
              <>
                <Zap className="w-5 h-5" />
                <span>Boost Now for ${PACKAGES.find(p => p.id === selectedPackage)?.price}</span>
              </>
            )}
          </button>
          
          <Link
            to="/my-listings"
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            Maybe later
          </Link>
        </div>

        {/* Info note */}
        <p className="text-center text-xs text-muted-foreground mt-6">
          By proceeding, you agree to our promotion terms. Promotions are non-refundable.
        </p>
      </main>

      <Footer />
    </div>
  );
};

export default PromoteListingPage;
